/**
 * Same-page hash jumps over `content-visibility: auto` sections.
 *
 * Every `section[data-section-id]` skips layout until it nears the viewport,
 * and a skipped section takes its remembered height (`contain-intrinsic-size:
 * auto`) or the 800px guess before its first render. A jump measures its
 * target from those guesses, then the sections it scrolls past render at
 * their real height and push the target away. So before a jump measures, the
 * sections above the target render in full (`data-jump-render`, global.css
 * § Section rendering). The mark clears once a frame has rendered: by then each
 * section has recorded its real height, and `auto` keeps it.
 *
 * Any script that measures a target before it scrolls calls `renderAbove`
 * first. The callers today:
 *
 * - `jump()` in AnchorNav (the sticky bar and sidebar).
 * - The document click listener `armInPageJump` registers (the plain pill
 *   strip, which goes through Lenis or the native fragment scroll, and any link
 *   to a hash on the current page, with or without its path).
 * - The `hashchange` listener it registers beside it (an edited address bar,
 *   and back and forward between hash URLs).
 * - The scripted scrolls: `wireToolSwitch` (`open` and `scrollToSwitch`),
 *   `RulesCheck` (`scrollTo` and the aside jump), and the preview-sync scroll
 *   (`restoreScrollAnchor` and `scrollToBlock`).
 * - The head script for a URL that loads with a hash.
 *
 * A new scripted scroll joins this list and calls `renderAbove` before it
 * measures.
 */

/** Set on a section the next measurement must see rendered. global.css reads it. */
const JUMP_ATTR = "data-jump-render"

/** Set on `<html>` while a page that loaded with a hash settles. global.css reads it. */
export const LOAD_ATTR = "data-jump-load"

/** `Node.DOCUMENT_POSITION_FOLLOWING`: the argument node comes after the one asked. */
const FOLLOWING = 4

/**
 * Whether a section sits above the target and so shifts it: the section is
 * the target, holds it, or comes before it. `position` is the section's
 * `compareDocumentPosition(target)`.
 */
export function rendersBefore(position: number, isTarget: boolean): boolean {
	return isTarget || (position & FOLLOWING) !== 0
}

/** The element id a same-page link's `href` names, or null for a bare `#` or a malformed escape. */
export function hashId(href: string): string | null {
	if (!href.startsWith("#")) return null
	try {
		return decodeURIComponent(href.slice(1)) || null
	} catch {
		return null
	}
}

/**
 * The element id a link names when it points at a spot on the page it sits
 * on: a bare `#id`, or a path written out (`/join#volunteer` on `/join`).
 * `here` is the current location. A link to another page, or to the same page
 * with another query, is null: it navigates away.
 */
export function samePageHashId(href: string, here: Pick<Location, "href">): string | null {
	if (href.startsWith("#")) return hashId(href)
	let url: URL
	let current: URL
	try {
		url = new URL(href, here.href)
		current = new URL(here.href)
	} catch {
		return null
	}
	if (url.origin !== current.origin || url.pathname !== current.pathname) return null
	if (url.search !== current.search) return null
	return hashId(url.hash)
}

let pendingFrame = 0

/** Drops every mark once two frames have run: the first renders the sections, the second follows it. */
function clearAfterFrame(doc: Document): void {
	const win = doc.defaultView
	if (!win) return
	win.cancelAnimationFrame(pendingFrame)
	pendingFrame = win.requestAnimationFrame(() => {
		pendingFrame = win.requestAnimationFrame(() => {
			for (const section of doc.querySelectorAll(`[${JUMP_ATTR}]`)) {
				section.removeAttribute(JUMP_ATTR)
			}
		})
	})
}

/**
 * Renders the sections above `target` ahead of the caller's measurement. The
 * attribute lands synchronously, so the style recalc a `getBoundingClientRect`
 * forces already sees it. Only the sections above are marked, which keeps a jump
 * near the top of a long page cheap. Lenis clamps a scroll to the page height
 * it last measured, so it measures again once the sections have grown.
 */
export function renderAbove(target: Element, doc: Document = document): void {
	for (const section of doc.querySelectorAll("section[data-section-id]")) {
		if (rendersBefore(section.compareDocumentPosition(target), section === target)) {
			section.setAttribute(JUMP_ATTR, "")
		}
	}
	doc.defaultView?.locomotive?.lenisInstance?.resize()
	clearAfterFrame(doc)
}

/**
 * Renders the sections above any same-page link's target before Lenis' anchor
 * handler or the native fragment scroll measures it. Capture phase on the
 * document, so it runs ahead of both. A click that will not scroll this page
 * (a modified click, a non-primary button) is left alone.
 *
 * A hash change with no click (an edited address bar, back, forward) can scroll
 * natively before `hashchange` fires. An edited hash scrolls from the guessed
 * heights. Back and forward restore the entry's saved scroll offset instead, and
 * that offset already holds the real heights. The listener measures the
 * target's top, renders the sections above it, measures again, and scrolls by
 * the difference. An edited hash moves by the height error, so it lands on the
 * target. A restored offset moves by about 0, so it stays where the reader was.
 * A hash change a click already prepared (marks still set) is left alone: its
 * scroll measured rendered sections.
 */
export function armInPageJump(doc: Document = document): void {
	doc.defaultView?.addEventListener("hashchange", () => {
		if (doc.querySelector(`[${JUMP_ATTR}]`)) return
		const id = hashId(doc.defaultView?.location.hash ?? "")
		const target = id ? doc.getElementById(id) : null
		if (!target) return
		const before = target.getBoundingClientRect().top
		renderAbove(target, doc)
		const shift = target.getBoundingClientRect().top - before
		if (shift !== 0) doc.defaultView?.scrollBy(0, shift)
	})
	doc.addEventListener(
		"click",
		(event) => {
			if (event.defaultPrevented || event.button !== 0) return
			if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return
			const link =
				event.target instanceof Element
					? event.target.closest<HTMLAnchorElement>("a[href*='#']")
					: null
			const view = doc.defaultView
			const id =
				link && view ? samePageHashId(link.getAttribute("href") ?? "", view.location) : null
			const target = id ? doc.getElementById(id) : null
			if (target) renderAbove(target, doc)
		},
		true,
	)
}

/**
 * Inline head script for a page that loads with a hash. The browser scrolls
 * to the fragment while the sections are still guesses, and the head cannot
 * name the target yet, so every section renders until the load has settled.
 * A string, like `RESUME_LINK_SCRIPT`: it has to run before first paint.
 * inPageJump.test.ts executes it.
 */
export const HASH_LOAD_SCRIPT = [
	"(function(w,d){",
	"var h=w.location.hash;if(h.length<2)return;",
	"var e=d.documentElement;",
	`e.setAttribute(${JSON.stringify(LOAD_ATTR)},"");`,
	"function done(){w.requestAnimationFrame(function(){w.requestAnimationFrame(function(){",
	`e.removeAttribute(${JSON.stringify(LOAD_ATTR)})`,
	"})})}",
	'if(d.readyState==="complete")done();else w.addEventListener("load",done,{once:true})',
	"})(window,document);",
].join("")
